import React, { useState, useEffect, useRef } from 'react';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Skeleton } from 'primereact/skeleton';
import { SelectButton } from 'primereact/selectbutton';
import { Message } from 'primereact/message';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import axios from '../config/axios.config';

// Current logged-in user (for deciding the solo->team upgrade flow).
const getCurrentUser = () => {
    try { return JSON.parse(localStorage.getItem('user')) || {}; }
    catch { return {}; }
};

const BILLING_OPTIONS = [
    { label: 'Monthly', value: 'monthly' },
    { label: 'Yearly', value: 'yearly' },
];

// Indian-format rupee, no paise (UI-UX money rule).
const formatINR = (amount) =>
    `₹${parseFloat(amount || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

const SubscriptionPlans = () => {
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [billing, setBilling] = useState('monthly');
    const [hasActiveSubscription, setHasActiveSubscription] = useState(false);
    const [subscribingId, setSubscribingId] = useState(null);

    // Solo -> team upgrade flow
    const currentUser = getCurrentUser();
    const isSoloUser = !currentUser.company_id && currentUser.user_type !== 'company_user';
    // Company employees inherit the owner's plan — they don't subscribe themselves.
    const isEmployee = currentUser.user_type === 'company_user';
    const [upgradeDialog, setUpgradeDialog] = useState(false);
    const [upgradePlan, setUpgradePlan] = useState(null);
    const [companyName, setCompanyName] = useState('');
    const [upgrading, setUpgrading] = useState(false);

    const toast = useRef(null);

    useEffect(() => {
        fetchPlans();
    }, []);

    const fetchPlans = async () => {
        try {
            setLoading(true);
            const response = await axios.get('/plans');
            if (response.data.success) {
                setPlans(response.data.data.plans || []);
                setHasActiveSubscription(!!response.data.data.hasActiveSubscription);
            }
        } catch (error) {
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to load subscription plans',
                life: 3000,
            });
        } finally {
            setLoading(false);
        }
    };

    const isContactSales = (plan) => Number(plan?.pricing?.monthly?.amount) <= 0;
    // A "team" plan allows more than one user (maxUsers !== 1; 0 = unlimited).
    const isTeamPlan = (plan) => Number(plan?.features?.maxUsers) !== 1;

    // The plan the user is currently on (for upgrade/downgrade comparison).
    const currentPlan = plans.find((p) => p.isCurrentPlan) || null;
    const priceOf = (plan) => Number((billing === 'monthly' ? plan?.pricing?.monthly?.amount : plan?.pricing?.yearly?.amount) || 0);
    // Relative to the current plan: 'upgrade' (costs more) or 'downgrade' (costs less).
    const changeKind = (plan) => {
        if (!currentPlan || plan.isCurrentPlan) return null;
        return priceOf(plan) > priceOf(currentPlan) ? 'upgrade' : 'downgrade';
    };

    const handleSubscribe = (plan) => {
        if (isContactSales(plan)) {
            toast.current?.show({
                severity: 'info',
                summary: 'Contact Sales',
                detail: 'Our team will reach out to tailor this plan for you.',
                life: 4000,
            });
            return;
        }
        // Solo user picking a team plan → must create a company first (promotion).
        if (isSoloUser && isTeamPlan(plan)) {
            setUpgradePlan(plan);
            setCompanyName('');
            setUpgradeDialog(true);
            return;
        }
        // Has an active sub → this is a plan CHANGE (upgrade or downgrade).
        if (hasActiveSubscription) {
            const kind = changeKind(plan);
            if (!kind) return; // current plan or indeterminate
            if (kind === 'upgrade') {
                confirmDialog({
                    message: `Upgrade to ${plan.name} (${billing})? You'll be charged a prorated amount for the rest of this billing cycle, and the extra credits are added immediately.`,
                    header: 'Confirm Upgrade',
                    icon: 'pi pi-arrow-up',
                    acceptLabel: 'Upgrade & Pay',
                    rejectLabel: 'Cancel',
                    accept: () => initiateChangePlan(plan, 'upgrade'),
                });
            } else {
                confirmDialog({
                    message: `Downgrade to ${plan.name} (${billing})? You keep your current plan until your next billing date, then switch automatically. No charge now.`,
                    header: 'Confirm Downgrade',
                    icon: 'pi pi-arrow-down',
                    acceptLabel: 'Schedule Downgrade',
                    rejectLabel: 'Cancel',
                    accept: () => initiateChangePlan(plan, 'downgrade'),
                });
            }
            return;
        }
        const price = billing === 'monthly' ? plan.pricing.monthly : plan.pricing.yearly;
        confirmDialog({
            message: `Subscribe to ${plan.name} (${billing}) for ${price.display}?`,
            header: 'Confirm Subscription',
            icon: 'pi pi-credit-card',
            acceptLabel: 'Subscribe',
            rejectLabel: 'Cancel',
            accept: () => initiateSubscription(plan),
        });
    };

    // Upgrade (prorated Razorpay payment) or downgrade (scheduled, no charge).
    const initiateChangePlan = async (plan, kind) => {
        setSubscribingId(plan.id);
        try {
            const initRes = await axios.post('/subscriptions/change-plan/initiate', { plan_id: plan.id });
            const data = initRes.data || {};
            if (!data.success) throw new Error(data.message || 'Could not start the plan change.');

            // Downgrade scheduled, or upgrade with nothing to charge → done.
            if (data.scheduled || data.applied) {
                toast.current?.show({
                    severity: 'success',
                    summary: kind === 'downgrade' ? 'Downgrade scheduled' : 'Plan changed',
                    detail: data.message,
                    life: 5000,
                });
                fetchPlans();
                setSubscribingId(null);
                return;
            }

            // Upgrade → prorated Razorpay checkout.
            const loaded = await loadRazorpayScript();
            if (!loaded) throw new Error('Failed to load the payment SDK. Please retry.');
            const token = data.payment_confirm_token;
            const options = {
                key: data.key,
                order_id: data.order_id,
                amount: data.amount,
                currency: data.currency || 'INR',
                name: data.name || 'Plan Upgrade',
                description: data.description || `Upgrade to ${plan.name}`,
                handler: async (response) => {
                    try {
                        const confirmRes = await axios.post('/subscriptions/change-plan/confirm', { ...response, payment_confirm_token: token });
                        if (confirmRes.data?.success) {
                            toast.current?.show({ severity: 'success', summary: 'Plan upgraded', detail: confirmRes.data.message || 'Your plan is now upgraded.', life: 4000 });
                            fetchPlans();
                        } else {
                            toast.current?.show({ severity: 'error', summary: 'Upgrade failed', detail: confirmRes.data?.message || 'Payment captured but upgrade failed. Contact support.', life: 6000 });
                        }
                    } catch (err) {
                        toast.current?.show({ severity: 'error', summary: 'Upgrade failed', detail: err.response?.data?.message || 'Payment captured but confirmation failed. Contact support.', life: 6000 });
                    } finally {
                        setSubscribingId(null);
                    }
                },
                modal: { ondismiss: () => { toast.current?.show({ severity: 'info', summary: 'Upgrade cancelled', life: 2500 }); setSubscribingId(null); } },
                theme: { color: '#1a73e8' },
            };
            new window.Razorpay(options).open();
        } catch (error) {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: error.response?.data?.message || error.message || 'Failed to change plan', life: 5000 });
            setSubscribingId(null);
        }
    };

    // Load the Razorpay checkout SDK on demand (same as the credit-recharge flow).
    const loadRazorpayScript = () => new Promise((resolve) => {
        if (window.Razorpay) { resolve(true); return; }
        const script = document.createElement('script');
        script.src = 'https://checkout.razorpay.com/v1/checkout.js';
        script.onload = () => resolve(true);
        script.onerror = () => resolve(false);
        document.body.appendChild(script);
    });

    // Subscribe via a real Razorpay payment — mirrors CreditRecharge:
    // initiate (create order) → open Razorpay → confirm (verify + activate).
    const initiateSubscription = async (plan) => {
        setSubscribingId(plan.id);
        try {
            const loaded = await loadRazorpayScript();
            if (!loaded) throw new Error('Failed to load the payment SDK. Please retry.');

            const initRes = await axios.post('/subscriptions/initiate', {
                plan_id: plan.id,
                billing_interval: billing,
            });
            if (!initRes.data?.success || !initRes.data?.order_id) {
                throw new Error(initRes.data?.message || 'Could not start the subscription payment.');
            }

            const paymentConfirmToken = initRes.data.payment_confirm_token;
            const options = {
                key: initRes.data.key,
                order_id: initRes.data.order_id,
                amount: initRes.data.amount,
                currency: initRes.data.currency || 'INR',
                name: initRes.data.name || 'Subscription',
                description: initRes.data.description || `${plan.name} subscription`,
                handler: async (response) => {
                    try {
                        const confirmRes = await axios.post('/subscriptions/confirm', {
                            ...response,
                            payment_confirm_token: paymentConfirmToken,
                        });
                        if (confirmRes.data?.success) {
                            toast.current?.show({
                                severity: 'success',
                                summary: 'Subscription activated',
                                detail: confirmRes.data.message || 'Your plan is now active.',
                                life: 4000,
                            });
                            fetchPlans();
                        } else {
                            toast.current?.show({
                                severity: 'error',
                                summary: 'Activation failed',
                                detail: confirmRes.data?.message || 'Payment captured but activation failed. Contact support.',
                                life: 6000,
                            });
                        }
                    } catch (confirmError) {
                        toast.current?.show({
                            severity: 'error',
                            summary: 'Activation failed',
                            detail: confirmError.response?.data?.message || 'Payment captured but confirmation failed. Contact support.',
                            life: 6000,
                        });
                    } finally {
                        setSubscribingId(null);
                    }
                },
                modal: {
                    ondismiss: () => {
                        toast.current?.show({ severity: 'info', summary: 'Payment cancelled', life: 2500 });
                        setSubscribingId(null);
                    },
                },
                theme: { color: '#1a73e8' },
            };

            const rzp = new window.Razorpay(options);
            rzp.open();
            // Note: setSubscribingId is cleared in the handler/ondismiss callbacks.
        } catch (error) {
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: error.response?.data?.message || error.message || 'Failed to start subscription',
                life: 4000,
            });
            setSubscribingId(null);
        }
    };

    // Solo -> team: create company, become company admin, subscribe to team plan.
    const submitUpgrade = async () => {
        if (!companyName.trim()) {
            toast.current?.show({ severity: 'warn', summary: 'Company name required', detail: 'Enter your company name to continue.', life: 3000 });
            return;
        }
        setUpgrading(true);
        try {
            const res = await axios.post('/plans/upgrade-to-team', {
                plan_id: upgradePlan.id,
                company_name: companyName.trim(),
                billing_interval: billing,
            });
            if (res.data.success) {
                toast.current?.show({
                    severity: 'success',
                    summary: 'Upgraded to team plan',
                    detail: res.data.message || 'You are now the company admin.',
                    life: 4000,
                });
                setUpgradeDialog(false);
                // Role/type changed — refresh so the app picks up company-admin access.
                setTimeout(() => window.location.reload(), 1200);
            }
        } catch (error) {
            toast.current?.show({
                severity: 'error',
                summary: 'Upgrade failed',
                detail: error.response?.data?.message || 'Could not upgrade to a team plan.',
                life: 5000,
            });
        } finally {
            setUpgrading(false);
        }
    };

    // One feature row — icon + label, muted when the feature is off.
    const featRow = (icon, label, on = true) => (
        <li className="flex align-items-center gap-2 mb-2">
            <i className={`pi ${icon} ${on ? 'text-primary' : 'text-400'}`} aria-hidden="true" />
            <span className={`text-sm ${on ? 'text-700' : 'text-400'}`}>{label}</span>
        </li>
    );

    const renderPlanCard = (plan) => {
        const contact = isContactSales(plan);
        const price = billing === 'monthly' ? plan.pricing.monthly : plan.pricing.yearly;
        const credits = billing === 'monthly'
            ? plan.features?.apiCredits?.monthly?.value
            : plan.features?.apiCredits?.yearly?.value;
        const maxUsers = plan.features?.maxUsers;
        const usersLabel = maxUsers === 0 ? 'Unlimited users' : `Up to ${maxUsers} user${maxUsers === 1 ? '' : 's'}`;
        const popular = !!plan.isPopular;

        // Real loan types this plan unlocks (e.g. "Personal Loan, Home Loan").
        const prettyLoan = (n) => String(n || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
        const loanTypes = (plan.features?.loanTypes || []).map(prettyLoan);
        const loanLabel = loanTypes.length ? `${loanTypes.join(', ')} offers` : 'Loan offers';

        return (
            <div className="col-12 md:col-6 xl:col-3 mb-3" key={plan.id}>
                <Card
                    className="h-full"
                    pt={{
                        root: {
                            // Every card carries the same primary border and
                            // elevation — the "Most Popular" tag is the only
                            // differentiator. (Border colour must come from
                            // border-*, never surface-*: those are BACKGROUND
                            // utilities with !important and would repaint the
                            // card grey over surface-0.)
                            className: 'h-full surface-0 border-2 border-primary border-round-xl shadow-2',
                        },
                        body: { className: 'h-full flex flex-column' },
                        content: { className: 'flex-grow-1 flex flex-column p-0' },
                    }}
                >
                    {/* Header */}
                    <div className="flex align-items-center justify-content-between mb-1">
                        <span className="text-base font-semibold text-900">{plan.name}</span>
                        {popular && <Tag value="Most Popular" severity="success" className="text-xs" />}
                        {plan.isCurrentPlan && !popular && <Tag value="Current" severity="info" className="text-xs" />}
                    </div>
                    {plan.description && (
                        <p className="text-500 text-xs m-0 mb-3" style={{ minHeight: '2rem' }}>{plan.description}</p>
                    )}

                    {/* Price */}
                    <div className="mb-1">
                        {contact ? (
                            <span className="text-xl font-semibold text-900">Let's talk</span>
                        ) : (
                            <span className="flex align-items-baseline gap-1">
                                <span className="text-xl font-semibold text-900">{price.display}</span>
                                <span className="text-500 text-sm">/{billing === 'monthly' ? 'mo' : 'yr'}</span>
                            </span>
                        )}
                    </div>
                    <div style={{ minHeight: '1.25rem' }} className="mb-3">
                        {!contact && billing === 'yearly' && plan.pricing.yearly.savings > 0 && (
                            <span className="text-green-600 text-xs font-medium">
                                Save {formatINR(plan.pricing.yearly.savings)} / year
                            </span>
                        )}
                        {contact && <span className="text-500 text-xs">Tailored to your network</span>}
                    </div>

                    {/* Headline stats */}
                    <div className="surface-ground border-round p-2 mb-3 flex justify-content-between align-items-center">
                        <span className="flex align-items-center gap-2 text-sm text-700">
                            <i className="pi pi-users text-primary" aria-hidden="true" />{usersLabel}
                        </span>
                        <span className="text-sm font-medium text-900">
                            {contact ? 'Custom' : `${(credits || 0).toLocaleString('en-IN')} cr`}
                        </span>
                    </div>

                    {/* Features */}
                    <ul className="list-none p-0 m-0 flex-grow-1">
                        {featRow('pi-check-circle', loanLabel)}
                        {featRow('pi-database', contact ? 'Custom credits' : `${(credits || 0).toLocaleString('en-IN')} credits / ${billing === 'monthly' ? 'month' : 'year'}`)}
                        {featRow('pi-cloud', plan.features?.storage?.display || 'Storage')}
                        {featRow('pi-chart-line', 'Advanced analytics', plan.features?.advancedAnalytics)}
                        {featRow('pi-star', 'Priority support', plan.features?.prioritySupport)}
                    </ul>

                    {/* CTA — employees inherit the owner's plan, so they get a
                        note instead of a dead-end "Choose plan" button. */}
                    {isEmployee ? (
                        <div className="mt-3 flex align-items-center justify-content-center gap-2 surface-ground border-round p-2 text-sm text-600">
                            <i className="pi pi-info-circle text-primary" aria-hidden="true" />
                            <span>Managed by your company admin</span>
                        </div>
                    ) : (() => {
                        // For an active subscriber, non-current plans become
                        // Upgrade/Downgrade; otherwise it's the normal Choose plan.
                        const kind = hasActiveSubscription ? changeKind(plan) : null;
                        const label = contact ? 'Contact Sales'
                            : plan.isCurrentPlan ? 'Current Plan'
                            : kind === 'upgrade' ? 'Upgrade'
                            : kind === 'downgrade' ? 'Downgrade'
                            : 'Choose plan';
                        const icon = contact ? 'pi pi-envelope'
                            : kind === 'upgrade' ? 'pi pi-arrow-up'
                            : kind === 'downgrade' ? 'pi pi-arrow-down'
                            : 'pi pi-arrow-right';
                        return (
                            <Button
                                label={label}
                                icon={icon}
                                iconPos="right"
                                className="w-full mt-3 font-semibold"
                                // Filled primary on every card. Outlined is now
                                // reserved for the one case where it carries
                                // meaning — a downgrade, which shouldn't read as
                                // the encouraged action.
                                outlined={kind === 'downgrade'}
                                severity={contact ? 'help' : kind === 'downgrade' ? 'secondary' : undefined}
                                disabled={plan.isCurrentPlan || contact}
                                loading={subscribingId === plan.id}
                                onClick={() => handleSubscribe(plan)}
                            />
                        );
                    })()}
                </Card>
            </div>
        );
    };

    const renderSkeleton = (k) => (
        <div className="col-12 md:col-6 xl:col-3 mb-3" key={k}>
            <Card pt={{ root: { className: 'surface-0 border-1 border-300 border-round-xl shadow-1' } }}>
                <Skeleton width="60%" height="1.5rem" className="mb-2" />
                <Skeleton width="40%" height="2rem" className="mb-3" />
                <Skeleton height="2.5rem" className="mb-3" />
                <Skeleton height="8rem" className="mb-3" />
                <Skeleton height="2.5rem" />
            </Card>
        </div>
    );

    return (
        <div className="grid">
            <Toast ref={toast} />
            <ConfirmDialog />

            <div className="col-12">
                {/* Header */}
                <div className="text-center mb-4">
                    <h1 className="text-xl font-semibold text-900 m-0 mb-1">Choose your plan</h1>
                    <p className="text-600 text-sm m-0">
                        Personal &amp; Home Loan offers, unlimited leads, and verification credits — pick the size that fits your team.
                    </p>
                </div>

                {/* Billing toggle */}
                <div className="flex justify-content-center align-items-center gap-2 mb-4">
                    <SelectButton
                        value={billing}
                        onChange={(e) => e.value && setBilling(e.value)}
                        options={BILLING_OPTIONS}
                        allowEmpty={false}
                    />
                    <Tag value="2 months free yearly" severity="success" className="text-xs" />
                </div>

                {/* Active subscription banner */}
                {hasActiveSubscription && !loading && (
                    <Message
                        severity="info"
                        className="w-full mb-3"
                        text="You have an active subscription. Upgrade anytime (prorated) or schedule a downgrade for your next billing date."
                    />
                )}

                {/* Plans grid */}
                <div className="grid">
                    {loading ? (
                        [0, 1, 2, 3].map(renderSkeleton)
                    ) : plans.length === 0 ? (
                        <div className="col-12">
                            <Message
                                severity="info"
                                className="w-full"
                                text="No subscription plans available right now. Please check back later."
                            />
                        </div>
                    ) : (
                        plans.map(renderPlanCard)
                    )}
                </div>
            </div>

            {/* Solo -> team: ask for company name, then promote + subscribe. */}
            <Dialog
                visible={upgradeDialog}
                onHide={() => !upgrading && setUpgradeDialog(false)}
                header="Upgrade to a team plan"
                modal
                draggable={false}
                style={{ width: '90vw', maxWidth: '440px' }}
            >
                <p className="text-600 text-sm mt-0 mb-3">
                    {upgradePlan ? `${upgradePlan.name} supports multiple users.` : ''} We'll create your
                    company and make you the admin so you can add team members.
                </p>
                <div className="field">
                    <label htmlFor="companyName" className="block font-medium text-sm mb-2">
                        Company name <span className="text-red-500">*</span>
                    </label>
                    <InputText
                        id="companyName"
                        value={companyName}
                        onChange={(e) => setCompanyName(e.target.value)}
                        placeholder="e.g. Acme Financial Services"
                        className="w-full"
                        maxLength={255}
                        autoFocus
                    />
                </div>
                <div className="flex justify-content-end gap-2 mt-4">
                    <Button label="Cancel" icon="pi pi-times" severity="secondary" outlined onClick={() => setUpgradeDialog(false)} disabled={upgrading} />
                    <Button label="Create company & subscribe" icon="pi pi-arrow-right" iconPos="right" onClick={submitUpgrade} loading={upgrading} />
                </div>
            </Dialog>
        </div>
    );
};

export default SubscriptionPlans;
